import { useState, useEffect } from "react";
//components
import Iconify from "~src/components/Iconify";
//@mui
import {
  Box,
  IconButton,
  DialogActions,
  DialogTitle,
  Stack,
  Button,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { BrandMappingFilters } from "~src/hooks/useBrandMappingV3";
import MultiCountrySelect from "~src/components/MultiCountrySelect";
import NumberRangeSelector from "~src/components/NumberRangeSelector";
import { CountryType } from "~src/data/countries";

type PropsType = {
  filters: BrandMappingFilters | undefined;
  closeDrawer: () => void;
  onCancel?: VoidFunction;
  onComplete: (filters: BrandMappingFilters) => void;
  maxFollowers: number | undefined;
  brandCountries?: CountryType[];
};

export default function FilterDrawer({
  filters,
  closeDrawer,
  onCancel,
  onComplete,
  maxFollowers,
  brandCountries,
}: PropsType) {
  const [localFilters, setLocalFilters] = useState(filters);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleSaveClick = (
    selectedLocalFilters: BrandMappingFilters | undefined,
  ) => {
    if (selectedLocalFilters) {
      onComplete(selectedLocalFilters);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalFilters((prevFilters) => ({
      ...prevFilters,
      has_running_ads: event.target.checked,
    }));
  };

  return (
    <Box sx={{
      width: isMobile ? "300px" : "500px"
    }}>
      <DialogTitle sx={{ display: "flex" }}>
        <Box>Filter Map of Brands</Box>
        <Box
          sx={{
            ml: "auto",
          }}
        >
          <IconButton onClick={closeDrawer}>
            <Iconify icon={"material-symbols:close"} width={28} height={28} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <MultiCountrySelect
            title="Brand Country"
            value={localFilters?.brand_country}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                brand_country: value as string[],
              }));
            }}
            countries={brandCountries}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={localFilters?.has_running_ads}
                onChange={handleCheckboxChange}
              />
            }
            label="Has Matching Ads"
          />
          <MultiCountrySelect
            title="Ad Country"
            value={localFilters?.ad_country}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                ad_country: value as string[],
              }));
            }}
          />

          <NumberRangeSelector
            title="Product Price"
            value={localFilters?.product_price_range}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                product_price_range: value,
              }));
            }}
          />

          <NumberRangeSelector
            title="Estimated Site Traffic"
            value={localFilters?.estimated_monthly_traffic}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                estimated_monthly_traffic: value,
              }));
            }}
          />

          <NumberRangeSelector
            title="Estimated Monthly Revenue"
            value={localFilters?.estimated_monthly_revenue}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                estimated_monthly_revenue: value,
              }));
            }}
          />

          <NumberRangeSelector
            title="Social Followers"
            value={localFilters?.social_followers}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                social_followers: value,
              }));
            }}
            rangeOptions={[
              { label: "Low Followers", min: 0, max: 10000 },
              { label: "Medium Followers", min: 10001, max: 50000 },
              {
                label: "High Followers",
                min: 50001,
                max: maxFollowers || 100000,
              },
            ]}
          />
        </Stack>
      </Box>
      <DialogActions sx={{ mb: 10 }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={() => handleSaveClick(localFilters)}
        >
          Apply
        </Button>
      </DialogActions>
    </Box>
  );
}
